import React, { useState, useMemo, useCallback } from "react";
import { Navigate, useOutlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useIntl, FormattedMessage } from "react-intl";
import { Utils } from "../utils/utils";
import { useGetSurveysQuery, useGetInboxStatsQuery } from "../services/nestjs-base-api";
import { useLegalAcceptance } from "../hooks/useLegalAcceptance";
import { useClassicRoot } from "../hooks/useDashboardConfig";
import { useMaturityMode } from "../hooks/useMaturityMode";
import { useCustomerSegmentationEnabled } from "../hooks/useCustomerSegmentationEnabled";
import { MaturityMenuTag } from "./MaturityTag";
import { LegalAcceptanceModal } from "./Legal";
import {
    Layout,
    Menu,
    Avatar,
    Dropdown,
    Button,
    Typography,
    Badge,
    Alert,
    theme,
    Drawer,
    Grid,
    FloatButton
} from 'antd';
import {
    DashboardOutlined,
    LineChartOutlined,
    BankOutlined,
    SettingOutlined,
    LogoutOutlined,
    MailOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
    FormOutlined,
    FundOutlined,
    RobotOutlined,
    CheckCircleOutlined,
    FilterOutlined,
    PieChartOutlined,
    CommentOutlined,
    CustomerServiceOutlined,
    BulbOutlined,
    QuestionCircleOutlined,
    MobileOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import '../styles/dashboard-responsive.css';

// Fixed: Changed to MailOutlined (envelope icon)

const { Header, Content, Sider, Footer } = Layout;
const { Text } = Typography;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
): MenuItem {
    return {
        key,
        icon,
        children,
        label,
    } as MenuItem;
}

export const ModernDashboardLayout = () => {
    const { user, logout } = useAuth();
    const intl = useIntl();
    const outlet = useOutlet();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
    const [mobileBannerDismissed, setMobileBannerDismissed] = useState(() =>
        localStorage.getItem('mobileBannerDismissed') === 'true'
    );
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const location = useLocation();
    // SYR-79: classic dashboard root — '/dashboard' (flag off) or '/old-dashboard'
    // (flag on, when the Marketer View owns /dashboard). Keeps self-nav in-shell.
    const classicRoot = useClassicRoot();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    // Fetch surveys for the organization
    const { data: surveys } = useGetSurveysQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: user?.organisation?.id || 0
        },
        { skip: !user?.jwtToken || !user?.organisation?.id }
    );

    // Fetch inbox stats for organization admins to show unread count in header
    const { data: inboxStats } = useGetInboxStatsQuery(undefined, {
        skip: !Utils.userIsOrganisationAdmin(user),
        pollingInterval: 30000, // Poll every 30 seconds to keep it updated
    });

    // Fetch maturity mode settings (Beta / Early Access)
    const { isBetaMode, isEarlyAccessMode } = useMaturityMode();
    const customerSegmentationEnabled = useCustomerSegmentationEnabled();

    // Check if user needs to accept legal documents
    const { needsAcceptance, pendingDocuments, markAsAccepted, isEnabled } = useLegalAcceptance({
        jwtToken: user?.jwtToken || null,
    });

    // Filter active surveys
    const activeSurveys = useMemo(() => {
        return surveys?.filter(survey => survey.status === 'active') || [];
    }, [surveys]);

    const handleLogout = useCallback(() => {
        logout();
    }, [logout]);

    const userMenuItems: MenuProps['items'] = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: intl.formatMessage({ id: 'layout.dashboard.user.profile' }),
        },
        {
            key: 'settings',
            icon: <SettingOutlined />,
            label: intl.formatMessage({ id: 'layout.dashboard.user.settings' }),
        },
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: intl.formatMessage({ id: 'layout.dashboard.user.signOut' }),
            danger: true,
        },
    ];

    const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
        if (key === 'logout') {
            handleLogout();
        } else if (key === 'profile') {
            navigate(`${classicRoot}/profile`);
        } else if (key === 'settings') {
            navigate(`${classicRoot}/organisation`);
        }
    };


    const menuItems: MenuItem[] = useMemo(() => {
        // Helper to wrap a menu label with a maturity tag
        const withTag = (label: string, type: 'beta' | 'earlyAccess', show: boolean) => {
            if (!show) return label;
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}>{label}</span>
                    <MaturityMenuTag type={type} />
                </span>
            );
        };

        // Results group
        const resultsChildren: MenuItem[] = [
            getItem(withTag(intl.formatMessage({ id: 'layout.dashboard.menu.dashboard' }), 'beta', isBetaMode), 'dashboard', <DashboardOutlined />),
            getItem(withTag(intl.formatMessage({ id: 'layout.dashboard.menu.visualization' }), 'beta', isBetaMode), 'visualization', <PieChartOutlined />),
            getItem(withTag(intl.formatMessage({ id: 'layout.dashboard.menu.statements' }), 'beta', isBetaMode), 'statements', <CommentOutlined />),
            getItem(intl.formatMessage({ id: 'layout.dashboard.menu.dataSelection' }), 'data-selection', <FilterOutlined />),
        ];

        // Intelligence group
        const intelligenceChildren: MenuItem[] = [
            getItem(withTag(intl.formatMessage({ id: 'layout.dashboard.menu.advice' }), 'beta', isBetaMode), 'advice', <BulbOutlined />),
            getItem(withTag(intl.formatMessage({ id: 'layout.dashboard.menu.aiExpert' }), 'earlyAccess', isEarlyAccessMode), 'ai-expert', <RobotOutlined />),
        ];

        // Improvement group
        const improvementChildren: MenuItem[] = [
            getItem(withTag(intl.formatMessage({ id: 'layout.dashboard.menu.trends' }), 'earlyAccess', isEarlyAccessMode), 'trends', <LineChartOutlined />),
            getItem(withTag(intl.formatMessage({ id: 'layout.dashboard.menu.benchmarking' }), 'earlyAccess', isEarlyAccessMode), 'benchmarking', <FundOutlined />),
        ];

        // Admin group
        const adminChildren: MenuItem[] = [];

        // Only show Manage Surveys menu to OrganisationAdmin users
        if (Utils.userIsOrganisationAdmin(user)) {
            const surveyMenuItems: MenuItem[] = [
                getItem(intl.formatMessage({ id: 'layout.dashboard.menu.allSurveys' }), 'surveys-list', null),
            ];

            // Add active surveys to the menu
            if (activeSurveys.length > 0) {
                surveyMenuItems.push({
                    type: 'divider',
                } as MenuItem);

                activeSurveys.forEach(survey => {
                    surveyMenuItems.push(
                        getItem(
                            <span style={{ fontSize: '13px' }}>
                                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                                {survey.name}
                            </span>,
                            `survey-${survey.hash}`,
                            null
                        )
                    );
                });

                surveyMenuItems.push({
                    type: 'divider',
                } as MenuItem);
            }

            adminChildren.push(
                getItem(intl.formatMessage({ id: 'layout.dashboard.menu.manageSurveys' }), 'surveys', <FormOutlined />, surveyMenuItems)
            );
        }

        if (Utils.userIsOrganisationAdmin(user) && customerSegmentationEnabled) {
            adminChildren.push(
                getItem(intl.formatMessage({ id: 'layout.dashboard.menu.customers', defaultMessage: 'Customers' }), 'customers', <UserOutlined />),
            );
        }

        adminChildren.push(
            getItem(intl.formatMessage({ id: 'layout.dashboard.menu.organization' }), 'organisation', <BankOutlined />),
            getItem(intl.formatMessage({ id: 'layout.dashboard.menu.help' }), 'support', <QuestionCircleOutlined />),
        );

        return [
            getItem(intl.formatMessage({ id: 'layout.dashboard.menu.group.results' }), 'group-results', <PieChartOutlined />, resultsChildren),
            getItem(intl.formatMessage({ id: 'layout.dashboard.menu.group.intelligence' }), 'group-intelligence', <BulbOutlined />, intelligenceChildren),
            getItem(intl.formatMessage({ id: 'layout.dashboard.menu.group.improvement' }), 'group-improvement', <LineChartOutlined />, improvementChildren),
            getItem(intl.formatMessage({ id: 'layout.dashboard.menu.group.admin' }), 'group-admin', <SettingOutlined />, adminChildren),
        ];
    }, [intl, user, activeSurveys, isBetaMode, isEarlyAccessMode, customerSegmentationEnabled]);

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        // Close mobile drawer when menu item is clicked
        if (isMobile) {
            setMobileDrawerVisible(false);
        }
        
        // Check if it's an active survey click
        if (e.key.startsWith('survey-')) {
            const surveyHash = e.key.replace('survey-', '');
            navigate(`${classicRoot}/surveys/${surveyHash}`);
            return;
        }

        switch (e.key) {
            case 'dashboard':
                navigate(`${classicRoot}`);
                break;
            case 'surveys-list':
                navigate(`${classicRoot}/surveys`);
                break;
            case 'data-selection':
                navigate(`${classicRoot}/analytics/data-selection`);
                break;
            case 'visualization':
                navigate(`${classicRoot}/analytics/visualization`);
                break;
            case 'statements':
                navigate(`${classicRoot}/analytics/statements`);
                break;
            case 'trends':
                navigate(`${classicRoot}/analytics/trends`);
                break;
            case 'organisation':
                navigate(`${classicRoot}/organisation`);
                break;
            case 'customers':
                navigate(`${classicRoot}/customers`);
                break;
            case 'benchmarking':
                navigate(`${classicRoot}/benchmarking`);
                break;
            case 'ai-expert':
                navigate(`${classicRoot}/ai-experts`);
                break;
            case 'advice':
                navigate(`${classicRoot}/advice`);
                break;
            case 'objectives':
                navigate(`${classicRoot}/objectives`);
                break;
            case 'tools':
                navigate(`${classicRoot}/tools`);
                break;
            case 'support':
                navigate(`${classicRoot}/support`);
                break;
        }
    };

    const getSelectedKeys = () => {
        const path = location.pathname;
        if (path.includes('/analytics/data-selection')) return ['data-selection'];
        if (path.includes('/analytics/visualization')) return ['visualization'];
        if (path.includes('/analytics/statements')) return ['statements'];
        if (path.includes('/analytics/trends')) return ['trends'];
        if (path.includes('/benchmarking')) return ['benchmarking'];
        if (path.includes('/customers')) return ['customers'];
        if (path.includes('/support')) return ['support'];
        if (path.includes('/ai-experts')) return ['ai-expert'];
        if (path.includes('/advice')) return ['advice'];
        if (path.includes('/objectives')) return ['objectives'];
        if (path.includes('/tools')) return ['tools'];
        if (path.includes('/organisation')) return ['organisation'];
        
        // Check if viewing a specific survey
        const surveyMatch = path.match(/\/surveys\/([^\/]+)$/);
        if (surveyMatch && surveyMatch[1]) {
            const surveyHash = surveyMatch[1];
            // Check if this is one of our active surveys
            const activeSurvey = activeSurveys.find(s => s.hash === surveyHash);
            if (activeSurvey) {
                return [`survey-${surveyHash}`];
            }
        }
        
        if (path.includes('/surveys')) return ['surveys-list'];
        return ['dashboard'];
    };

    const getDefaultOpenKeys = () => {
        const path = location.pathname;
        const keys: string[] = [];

        // Always determine which group should be open based on current route
        if (path.includes('/advice') || path.includes('/ai-experts')) {
            keys.push('group-intelligence');
        } else if (path.includes('/objectives') || path.includes('/analytics/trends') || path.includes('/benchmarking')) {
            keys.push('group-improvement');
        } else if (path.includes('/surveys') || path.includes('/tools') || path.includes('/organisation') || path.includes('/support')) {
            keys.push('group-admin');
            if (path.includes('/surveys')) keys.push('surveys');
        } else {
            // Default: Results open (dashboard, visualization, statements, data-selection)
            keys.push('group-results');
        }

        return keys;
    };

    if (!user) {
        // Store the current URL for redirect after login
        const redirectUrl = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/sign-in?redirect=${redirectUrl}`} />;
    }

    // Redirect platform admins to admin dashboard
    if (Utils.userIsPlatformAdmin(user)) {
        return <Navigate to="/admin/dashboard" replace />;
    }

    // Redirect agency users (not in client context) to agency dashboard
    if (Utils.userIsAgencyUser(user) && !user.agencyContext) {
        return <Navigate to="/agency" replace />;
    }

    const logoStyle = {
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        marginBottom: '16px',
    };

    const logoIconStyle = {
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    // Inline SVG logo component for crisp rendering
    const LogoIcon = ({ size = 36 }: { size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" stroke="#3B82F6" strokeWidth="2" opacity="0.3"/>
            <circle cx="24" cy="24" r="10" fill="#F59E0B"/>
            <circle cx="24" cy="4" r="4" fill="#3B82F6"/>
            <circle cx="6" cy="34" r="4" fill="#22C55E"/>
            <circle cx="42" cy="34" r="4" fill="#3B82F6"/>
        </svg>
    );

    const headerStyle = {
        background: '#ffffff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    };

    const siderStyle = {
        background: '#ffffff',
        borderRight: `1px solid ${token.colorBorderSecondary}`,
    };

    const renderSidebarContent = () => (
        <>
            <div style={{ ...logoStyle, cursor: 'pointer' }} onClick={() => navigate(`${classicRoot}`)}>
                <div style={logoIconStyle}>
                    <LogoIcon size={36} />
                </div>
                {(!collapsed || isMobile) && (
                    <div>
                        <div style={{ fontWeight: '700', fontSize: '18px' }}>
                            <span style={{ color: '#1f2937' }}>BrandScience</span>
                            <span style={{ color: '#94A3B8' }}>.ai</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {intl.formatMessage({ id: 'layout.dashboard.tagline' })}
                        </div>
                    </div>
                )}
            </div>
            
            <Menu
                mode="inline"
                selectedKeys={getSelectedKeys()}
                defaultOpenKeys={getDefaultOpenKeys()}
                items={menuItems}
                onClick={handleMenuClick}
                style={{
                    border: 'none',
                    fontSize: '14px',
                }}
            />
        </>
    );

    return (
        <Layout style={{ minHeight: '100vh', background: '#f8fafc' }}>
            {!isMobile && (
                <Sider 
                    collapsible 
                    collapsed={collapsed} 
                    onCollapse={setCollapsed}
                    style={siderStyle}
                    width={280}
                    collapsedWidth={80}
                    theme="light"
                >
                    {renderSidebarContent()}
                </Sider>
            )}
            
            {isMobile && (
                <Drawer
                    placement="left"
                    closable={false}
                    onClose={() => setMobileDrawerVisible(false)}
                    open={mobileDrawerVisible}
                    width={280}
                    styles={{
                        body: { padding: 0, backgroundColor: '#ffffff' },
                        header: { display: 'none' },
                    }}
                >
                    {renderSidebarContent()}
                </Drawer>
            )}
            
            <Layout>
                <Header style={{
                    ...headerStyle,
                    padding: isMobile ? '0 16px' : '0 24px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Button
                            type="text"
                            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
                            onClick={() => isMobile ? setMobileDrawerVisible(true) : setCollapsed(!collapsed)}
                            style={{
                                fontSize: '16px',
                                width: 40,
                                height: 40,
                            }}
                        />
                        <div>
                            <Text style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                                {user.organisation?.name || intl.formatMessage({ id: 'layout.dashboard.brandName' })}
                            </Text>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Badge
                            count={Utils.userIsOrganisationAdmin(user) ? (inboxStats?.unread || 0) : 0}
                            size="small"
                            showZero={false}
                        >
                            <Button
                                type="text"
                                icon={<MailOutlined />}
                                onClick={() => navigate(`${classicRoot}/profile?tab=${Utils.userIsOrganisationAdmin(user) ? 'messages' : 'notifications'}`)}
                                style={{
                                    fontSize: '16px',
                                    width: 40,
                                    height: 40,
                                }}
                            />
                        </Badge>
                        
                        <Dropdown
                            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                            placement="bottomRight"
                            trigger={['click']}
                        >
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                cursor: 'pointer',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <Avatar
                                    style={{
                                        background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
                                    }}
                                    icon={<UserOutlined />}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Text style={{ fontWeight: '500', color: '#1f2937', lineHeight: 1.2 }}>
                                        {user.email?.split('@')[0] || 'User'}
                                    </Text>
                                    <Text style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.2 }}>
                                        {intl.formatMessage({ id: 'layout.dashboard.roleAdmin' })}
                                    </Text>
                                </div>
                            </div>
                        </Dropdown>
                    </div>
                </Header>
                
                <Content style={{
                    margin: isMobile ? '16px' : '24px',
                    background: 'transparent',
                }}>
                    {isMobile && !mobileBannerDismissed && (
                        <Alert
                            type="info"
                            showIcon
                            icon={<MobileOutlined />}
                            message={intl.formatMessage({ id: 'onboarding.mobileBanner.title' })}
                            description={intl.formatMessage({ id: 'onboarding.mobileBanner.description' })}
                            style={{ marginBottom: '16px', borderRadius: '12px' }}
                            closable
                            onClose={() => {
                                setMobileBannerDismissed(true);
                                localStorage.setItem('mobileBannerDismissed', 'true');
                            }}
                        />
                    )}
                    {outlet}
                </Content>

                <Footer style={{
                    textAlign: 'center',
                    background: 'transparent',
                    padding: isMobile ? '16px' : '24px',
                    borderTop: '1px solid #e5e7eb',
                }}>
                    {isEnabled && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: isMobile ? '16px' : '24px',
                            marginBottom: '8px',
                            flexWrap: 'wrap',
                        }}>
                            <Link
                                to="/privacy-policy"
                                style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#F59E0B'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                            >
                                {intl.formatMessage({ id: 'auth.footer.privacyPolicy' })}
                            </Link>
                            <Link
                                to="/terms-of-service"
                                style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#F59E0B'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                            >
                                {intl.formatMessage({ id: 'auth.footer.termsOfService' })}
                            </Link>
                        </div>
                    )}
                    <Text style={{ color: '#9ca3af', fontSize: '12px' }}>
                        <FormattedMessage id="auth.footer.copyright" values={{ year: new Date().getFullYear(), syriant: (chunks: React.ReactNode) => <a href="https://syriant.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{chunks}</a> }} />
                    </Text>
                </Footer>
            </Layout>

            {/* Floating Help Button - hide when already on support page */}
            {!location.pathname.includes('/support') && (
                <FloatButton
                    icon={<CustomerServiceOutlined />}
                    type="primary"
                    style={{
                        right: isMobile ? 16 : 24,
                        bottom: isMobile ? 16 : 24,
                        width: isMobile ? 56 : 64,
                        height: isMobile ? 56 : 64,
                    }}
                    badge={{ dot: false }}
                    tooltip="Need help? Contact Support"
                    onClick={() => navigate(`${classicRoot}/support`)}
                />
            )}

            {/* Legal Document Acceptance Modal */}
            <LegalAcceptanceModal
                visible={needsAcceptance}
                onAccepted={markAsAccepted}
                pendingDocuments={pendingDocuments}
                jwtToken={user?.jwtToken || ''}
                acceptanceMethod="login_prompt"
            />
        </Layout>
    );
};